/** Agent Perception → Live Perception + Psychologist Review option catalogs. */

export const LIVE_PERCEPTION_LANGUAGE_OPTIONS = [
  {
    value: "cerebras-gpt-oss-120b",
    label: "Cerebras gpt-oss-120b",
    hint: "Pros: fastest language affect (~0.4s), solid closed-set labels. Cons: thinner cues than Gemini.",
  },
  {
    value: "gemini-3.1-flash-lite",
    label: "Gemini 3.1 Flash-Lite",
    hint: "Pros: richest language cues, reliable JSON. Cons: ~1.2s, needs Gemini key.",
  },
] as const;

export const LIVE_PERCEPTION_VOICE_OPTIONS = [
  {
    value: "local",
    label: "Local (emotion2vec+ + w2v2)",
    hint: "Pros: on-box labels when models ship on the worker. Cons: currently skipped on the worker image (voice stays skipped until models land).",
  },
  {
    value: "local_oruk",
    label: "Local + Oruk",
    hint: "Pros: richer multilabel from a short mic window at each user turn; Oruk is highest fuse priority (including neutral). Cons: ~1.5s cloud RTT, needs ORUK_API_KEY.",
  },
] as const;

export const LIVE_PERCEPTION_VIDEO_OPTIONS = [
  {
    value: "gpt-4.1-mini",
    label: "gpt-4.1-mini (frames)",
    hint: "Pros: best face/gesture read on actor peaks (~94%). Cons: over-fires frustration on resting face unless structural cues are gated.",
  },
  {
    value: "gemini-3.1-flash-lite",
    label: "Gemini 3.1 Flash-Lite (clip)",
    hint: "Pros: native short clip, ~1.3s, same key as language alt. Cons: weaker live affect than gpt-4.1-mini (~81% peaks).",
  },
] as const;

/** CED soft-floors near ~0.50 on silence; default matches listen_mic. */
export const ACOUSTIC_EVENTS_THRESHOLD_DEFAULT = 0.55;
export const ACOUSTIC_EVENTS_THRESHOLD_MIN = 0.35;
export const ACOUSTIC_EVENTS_THRESHOLD_MAX = 0.95;
export const ACOUSTIC_EVENTS_COOLDOWN_DEFAULT = 12;
export const ACOUSTIC_EVENTS_COOLDOWN_MIN = 3;
export const ACOUSTIC_EVENTS_COOLDOWN_MAX = 120;

export const ACOUSTIC_EVENT_MODES = [
  "observe",
  "context",
  "respond_if_idle",
  "respond",
] as const;

export type AcousticEventMode = (typeof ACOUSTIC_EVENT_MODES)[number];

export type AcousticEventPolicy = {
  label: string;
  mode: AcousticEventMode;
  instruction?: string;
  ttsEmotion?: string | null;
  cooldownSec?: number;
  /**
   * Optional min CED score for this label. Omit to inherit the agent default
   * threshold (keeps Safe defaults quiet except where we set Music higher).
   */
  threshold?: number;
  /** When set, prepend this Cartesia/provider nonverbal token to spoken TTS (e.g. [laughter]). */
  insertNonverbal?: string | null;
};

export const ACOUSTIC_EVENT_MODE_OPTIONS: {
  value: AcousticEventMode;
  label: string;
  hint: string;
}[] = [
  {
    value: "observe",
    label: "Log only",
    hint: "Detect and write to events.jsonl. Conversation bot does not react.",
  },
  {
    value: "context",
    label: "Context",
    hint: "Silent note for the next user turn. No speech.",
  },
  {
    value: "respond_if_idle",
    label: "Respond when idle",
    hint: "Speak only if the bot is not already talking; otherwise treat as Context.",
  },
  {
    value: "respond",
    label: "Respond always (interrupt)",
    hint: "Hard-interrupt current bot speech, then reply.",
  },
];

const RESPOND_IDLE: AcousticEventMode = "respond_if_idle";
const OBSERVE: AcousticEventMode = "observe";

/** Safe starter policies (first enable when policies empty). */
export const ACOUSTIC_EVENTS_DEFAULT_POLICIES: AcousticEventPolicy[] = [
  { label: "Laughter", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Baby laughter", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Giggle", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Snicker", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Belly laugh", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Chuckle, chortle", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Cough", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Car", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Breathing", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Sigh", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Gasp", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Pant", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Snort", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Sniff", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  // Music false-positives near ~0.61 on quiet speech — require a higher bar.
  {
    label: "Music",
    mode: OBSERVE,
    cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT,
    threshold: 0.75,
  },
  { label: "Chewing, mastication", mode: OBSERVE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
  { label: "Knock", mode: RESPOND_IDLE, cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT },
];

/** @deprecated Prefer ACOUSTIC_EVENTS_DEFAULT_POLICIES — labels only for LP CED. */
export const ACOUSTIC_EVENTS_DEFAULT_WHITELIST = ACOUSTIC_EVENTS_DEFAULT_POLICIES.map(
  (p) => p.label,
);

export const LIVE_PERCEPTION_DEFAULTS = {
  livePerceptionEnabled: false,
  livePerceptionLanguageModel: "cerebras-gpt-oss-120b",
  livePerceptionVoiceMode: "local",
  livePerceptionVideoModel: "gpt-4.1-mini",
  livePerceptionDebugFramesInTranscript: false,
  livePerceptionAcousticEventsEnabled: false,
  livePerceptionAcousticEventsPolicies: [] as AcousticEventPolicy[],
  livePerceptionAcousticEventsWhitelist: [] as string[],
  livePerceptionAcousticEventsThreshold: ACOUSTIC_EVENTS_THRESHOLD_DEFAULT,
} as const;

export function clampAcousticEventsThreshold(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return ACOUSTIC_EVENTS_THRESHOLD_DEFAULT;
  return Math.min(
    ACOUSTIC_EVENTS_THRESHOLD_MAX,
    Math.max(ACOUSTIC_EVENTS_THRESHOLD_MIN, n),
  );
}

export function clampAcousticCooldownSec(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return ACOUSTIC_EVENTS_COOLDOWN_DEFAULT;
  return Math.min(
    ACOUSTIC_EVENTS_COOLDOWN_MAX,
    Math.max(ACOUSTIC_EVENTS_COOLDOWN_MIN, Math.round(n)),
  );
}

export function isAcousticEventMode(value: unknown): value is AcousticEventMode {
  return (
    typeof value === "string" &&
    (ACOUSTIC_EVENT_MODES as readonly string[]).includes(value)
  );
}

/** Normalize policies; accept legacy string[] whitelist as observe-only. */
export function normalizeAcousticEventPolicies(raw: unknown): AcousticEventPolicy[] {
  if (!raw) return [];
  const items = Array.isArray(raw) ? raw : [];
  const out: AcousticEventPolicy[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (typeof item === "string") {
      const label = item.trim();
      if (!label || seen.has(label)) continue;
      seen.add(label);
      out.push({
        label,
        mode: "observe",
        cooldownSec: ACOUSTIC_EVENTS_COOLDOWN_DEFAULT,
      });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const src = item as Record<string, unknown>;
    const label = String(src.label || "").trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    const mode: AcousticEventMode = isAcousticEventMode(src.mode) ? src.mode : "observe";
    const instruction =
      typeof src.instruction === "string" ? src.instruction.trim().slice(0, 2000) : "";
    const emotionRaw =
      src.ttsEmotion == null || src.ttsEmotion === ""
        ? null
        : String(src.ttsEmotion).trim().slice(0, 40);
    const nonverbalRaw =
      src.insertNonverbal == null || src.insertNonverbal === ""
        ? null
        : String(src.insertNonverbal).trim().slice(0, 40);
    const policy: AcousticEventPolicy = {
      label,
      mode,
      cooldownSec: clampAcousticCooldownSec(src.cooldownSec),
    };
    if (instruction) policy.instruction = instruction;
    if (emotionRaw) policy.ttsEmotion = emotionRaw;
    if (nonverbalRaw) policy.insertNonverbal = nonverbalRaw;
    if (src.threshold != null && src.threshold !== "") {
      policy.threshold = clampAcousticEventsThreshold(src.threshold);
    }
    out.push(policy);
  }
  return out.slice(0, 527);
}

export function acousticPolicyLabels(policies: AcousticEventPolicy[]): string[] {
  return policies.map((p) => p.label);
}

export function hintFor(
  options: readonly { value: string; hint: string }[],
  value: string | undefined,
  fallback: string,
) {
  return options.find((o) => o.value === value)?.hint || fallback;
}
